import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient
} from '@tanstack/react-query';
import { deleteAttachment, listAttachments, uploadAttachment } from '../api/attachments';
import { addComment, deleteComment, listComments } from '../api/comments';
import {
  createProject,
  createSection,
  deleteProject,
  listProjects,
  setProjectShares,
  updateProject
} from '../api/projects';
import { createTag, deleteTag, listTags, updateTag } from '../api/tags';
import {
  completeTask,
  createTask,
  deleteTask,
  getTask,
  listCompletedTasks,
  listTasks,
  reorderTasks,
  sortTodayByIce,
  uncompleteTask,
  updateTask
} from '../api/tasks';
import { getMe, patchPrefs } from '../api/users';
import { inviteMember, listWorkspaces } from '../api/workspaces';
import type {
  AttachmentDto,
  CommentDto,
  CompletedPageDto,
  CreateProjectRequest,
  CreateSectionRequest,
  CreateTagRequest,
  CreateTaskRequest,
  TaskDto,
  TaskListParams,
  UpdateProjectRequest,
  UpdateTaskRequest
} from '../api/types';

export const keys = {
  me: ['me'] as const,
  workspaces: ['workspaces'] as const,
  projects: (wid: string) => ['projects', wid] as const,
  tags: (wid: string) => ['tags', wid] as const,
  tasks: (wid: string, params: TaskListParams) =>
    ['tasks', wid, params.view ?? null, params.projectId ?? null, params.tagId ?? null] as const,
  completed: (wid: string) => ['tasks-completed', wid] as const,
  task: (id: string) => ['task', id] as const,
  comments: (taskId: string) => ['comments', taskId] as const,
  attachments: (taskId: string) => ['attachments', taskId] as const
};

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: getMe, staleTime: 5 * 60_000 });
}

export function useWorkspaces(enabled = true) {
  return useQuery({
    queryKey: keys.workspaces,
    queryFn: listWorkspaces,
    staleTime: 5 * 60_000,
    enabled
  });
}

export function useProjects(wid: string) {
  return useQuery({
    queryKey: keys.projects(wid),
    queryFn: () => listProjects(wid),
    staleTime: 30_000
  });
}

export function useTags(wid: string) {
  return useQuery({ queryKey: keys.tags(wid), queryFn: () => listTags(wid), staleTime: 30_000 });
}

export function useViewTasks(wid: string, params: TaskListParams, enabled = true) {
  return useQuery({
    queryKey: keys.tasks(wid, params),
    queryFn: () => listTasks(wid, params),
    staleTime: 10_000,
    enabled
  });
}

/** Workspace-wide, or one project's history when projectId is given. */
export function useCompletedTasks(wid: string, projectId?: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...keys.completed(wid), projectId ?? 'all'],
    queryFn: ({ pageParam }) => listCompletedTasks(wid, pageParam, 50, projectId),
    initialPageParam: 0,
    getNextPageParam: (last: CompletedPageDto) => (last.hasMore ? last.page + 1 : undefined),
    staleTime: 10_000,
    enabled
  });
}

export function useTask(taskId: string | null) {
  return useQuery({
    queryKey: keys.task(taskId ?? 'none'),
    queryFn: () => getTask(taskId as string),
    enabled: Boolean(taskId)
  });
}

export function useComments(taskId: string | null) {
  return useQuery({
    queryKey: keys.comments(taskId ?? 'none'),
    queryFn: () => listComments(taskId as string),
    enabled: Boolean(taskId)
  });
}

export function useAttachments(taskId: string | null) {
  return useQuery({
    queryKey: keys.attachments(taskId ?? 'none'),
    queryFn: () => listAttachments(taskId as string),
    enabled: Boolean(taskId)
  });
}

function patchTaskEverywhere(qc: QueryClient, taskId: string, patch: Partial<TaskDto>) {
  qc.setQueriesData<TaskDto[]>({ queryKey: ['tasks'] }, (old) =>
    old ? old.map((t) => (t.id === taskId ? { ...t, ...patch } : t)) : old
  );
  qc.setQueriesData<InfiniteData<CompletedPageDto>>({ queryKey: ['tasks-completed'] }, (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((t) => (t.id === taskId ? { ...t, ...patch } : t))
          }))
        }
      : old
  );
  qc.setQueryData<TaskDto>(keys.task(taskId), (old) => (old ? { ...old, ...patch } : old));
}

function removeTaskEverywhere(qc: QueryClient, taskId: string) {
  qc.setQueriesData<TaskDto[]>({ queryKey: ['tasks'] }, (old) =>
    old ? old.filter((t) => t.id !== taskId) : old
  );
  qc.setQueriesData<InfiniteData<CompletedPageDto>>({ queryKey: ['tasks-completed'] }, (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((t) => t.id !== taskId)
          }))
        }
      : old
  );
}

function invalidateTaskData(qc: QueryClient, taskId?: string) {
  qc.invalidateQueries({ queryKey: ['tasks'] });
  qc.invalidateQueries({ queryKey: ['tasks-completed'] });
  if (taskId) {
    qc.invalidateQueries({ queryKey: keys.task(taskId) });
  }
}

export function useCreateTask(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskRequest) => createTask(wid, body),
    onSuccess: () => invalidateTaskData(qc)
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, patch }: { taskId: string; patch: UpdateTaskRequest }) =>
      updateTask(taskId, patch),
    onMutate: async ({ taskId, patch }) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      await qc.cancelQueries({ queryKey: keys.task(taskId) });
      patchTaskEverywhere(qc, taskId, patch as Partial<TaskDto>);
    },
    onSettled: (_data, _err, { taskId }) => invalidateTaskData(qc, taskId)
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => completeTask(taskId),
    onMutate: async (taskId) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      const completedAt = new Date().toISOString();
      patchTaskEverywhere(qc, taskId, { completedAt });
      qc.setQueriesData<TaskDto[]>({ queryKey: ['tasks'] }, (old) =>
        old ? old.filter((t) => t.id !== taskId) : old
      );
    },
    onSettled: (_data, _err, taskId) => invalidateTaskData(qc, taskId)
  });
}

export function useUncompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => uncompleteTask(taskId),
    onMutate: async (taskId) => {
      await qc.cancelQueries({ queryKey: ['tasks-completed'] });
      patchTaskEverywhere(qc, taskId, { completedAt: null, completedBy: null });
      qc.setQueriesData<InfiniteData<CompletedPageDto>>(
        { queryKey: ['tasks-completed'] },
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  items: page.items.filter((t) => t.id !== taskId)
                }))
              }
            : old
      );
    },
    onSettled: (_data, _err, taskId) => invalidateTaskData(qc, taskId)
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => deleteTask(taskId),
    onMutate: async (taskId) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      removeTaskEverywhere(qc, taskId);
    },
    onSettled: () => invalidateTaskData(qc)
  });
}

export function useReorderTasks(wid: string, params: TaskListParams) {
  const qc = useQueryClient();
  const list = params.view === 'today' ? 'today' : 'container';
  return useMutation({
    mutationFn: (orderedTasks: TaskDto[]) =>
      reorderTasks(
        wid,
        orderedTasks.map((t) => t.id),
        list
      ),
    onMutate: async (orderedTasks) => {
      await qc.cancelQueries({ queryKey: keys.tasks(wid, params) });
      qc.setQueryData<TaskDto[]>(keys.tasks(wid, params), orderedTasks);
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: keys.tasks(wid, params) });
    }
  });
}

export function useSortTodayByIce(wid: string) {
  const qc = useQueryClient();
  const params: TaskListParams = { view: 'today' };
  return useMutation({
    mutationFn: () => sortTodayByIce(wid),
    onSuccess: (sorted) => {
      qc.setQueryData<TaskDto[]>(keys.tasks(wid, params), sorted);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.tasks(wid, params) })
  });
}

export function useCreateProject(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProjectRequest) => createProject(wid, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.projects(wid) })
  });
}

export function useUpdateProject(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, patch }: { projectId: string; patch: UpdateProjectRequest }) =>
      updateProject(projectId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.projects(wid) });
      invalidateTaskData(qc);
    }
  });
}

export function useSetProjectShares(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, userIds }: { projectId: string; userIds: string[] }) =>
      setProjectShares(projectId, userIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.projects(wid) });
      invalidateTaskData(qc);
    }
  });
}

export function useInviteMember(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => inviteMember(wid, email),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.workspaces })
  });
}

export function usePatchPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) => patchPrefs(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me })
  });
}

export function useDeleteProject(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => deleteProject(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.projects(wid) });
      invalidateTaskData(qc);
    }
  });
}

export function useCreateSection(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, body }: { projectId: string; body: CreateSectionRequest }) =>
      createSection(projectId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.projects(wid) })
  });
}

export function useCreateTag(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTagRequest) => createTag(wid, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tags(wid) })
  });
}

export function useUpdateTag(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tagId, patch }: { tagId: string; patch: { name?: string; color?: string } }) =>
      updateTag(tagId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tags(wid) })
  });
}

export function useDeleteTag(wid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => deleteTag(tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.tags(wid) });
      invalidateTaskData(qc);
    }
  });
}

export function useAddComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => addComment(taskId, body),
    onSuccess: (comment: CommentDto) => {
      qc.setQueryData<CommentDto[]>(keys.comments(taskId), (old) =>
        old ? [...old, comment] : [comment]
      );
    }
  });
}

export function useDeleteComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteComment(commentId),
    onMutate: async (commentId) => {
      await qc.cancelQueries({ queryKey: keys.comments(taskId) });
      qc.setQueryData<CommentDto[]>(keys.comments(taskId), (old) =>
        old ? old.filter((c) => c.id !== commentId) : old
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.comments(taskId) })
  });
}

export function useUploadAttachment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadAttachment(taskId, file),
    onSuccess: (attachment: AttachmentDto) => {
      qc.setQueryData<AttachmentDto[]>(keys.attachments(taskId), (old) =>
        old ? [...old, attachment] : [attachment]
      );
    }
  });
}

export function useDeleteAttachment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(attachmentId),
    onMutate: async (attachmentId) => {
      await qc.cancelQueries({ queryKey: keys.attachments(taskId) });
      qc.setQueryData<AttachmentDto[]>(keys.attachments(taskId), (old) =>
        old ? old.filter((a) => a.id !== attachmentId) : old
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.attachments(taskId) })
  });
}
